import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LopRecordForm,
  type LopFieldDef,
} from '../../components/LopRecordForm';
import { Hash, Layers, Coins } from 'lucide-react';
import { useTabTitle } from '../../tabs';
import { getAsset, saveAssetsBatch } from '../../api/assets';
import { useAssetTypes } from '../../hooks/useAssetTypes';
import { useCrcies } from '../../hooks/useCrcies';
import { useBenchmarkRates } from '../../hooks/useBenchmarkRates';
import { useBenchmarkRatePeriods } from '../../hooks/useBenchmarkRatePeriods';
import { useIndices } from '../../hooks/useIndices';
import { useBaseAssetTypes } from '../../hooks/useBaseAssetTypes';
import { useCreditRiskTypes } from '../../hooks/useCreditRiskTypes';
import type { Asset } from '../../types/Asset';
import { isAssetFieldApplicable } from '../../config/assetTypeFields';
import { t } from '../../i18n/t';

const newAsset = (): Asset => ({
  assetId: 0,
  assetName: null,
  assetTypeId: 0,
  symbol: null,
  crcyId: null,
  benchmarkRateId: null,
  benchmarkRatePeriodId: null,
  expDate: null,
  idxId: null,
  multiplier: null,
  isForeign: null,
  linkageCrcyId: null,
  baseAssetTypeId: null,
  creditRiskTypeId: null,
  ticker: null,
  shortName: null,
  isin: null,
  isin4Isa: null,
  bloombergTradePlaceId: null,
  isPreMarketExchange: null,
  isDynamicInterest: null,
  countryCode: null,
  industryGroupId: null,
  industrySectorId: null,
  isGovernment: null,
  strike: null,
  value1Point: null,
  callOrPut: null,
  underlyingAssetId: null,
  assetSource: 'central',
});

export function AssetRecordPage() {
  const [searchParams] = useSearchParams();
  const idParam = searchParams.get('id');
  const initialId = useMemo(() => {
    if (idParam == null) return null;
    const n = Number(idParam);
    return Number.isFinite(n) ? n : null;
  }, [idParam]);

  // Internal tabs (plan §3.6): override the router-derived default title once
  // a real record loads (or the user goes back to "new") — see the matching
  // comment in FundRecordPage.
  const [tabTitle, setTabTitle] = useState<string | null>(null);
  useTabTitle(tabTitle);

  const {
    data: assetTypes,
    loading: assetTypesLoading,
    error: assetTypesError,
  } = useAssetTypes();
  const {
    data: crcies,
    loading: crciesLoading,
    error: crciesError,
  } = useCrcies();
  const {
    data: benchmarkRates,
    loading: benchmarkRatesLoading,
    error: benchmarkRatesError,
  } = useBenchmarkRates();
  const {
    data: benchmarkRatePeriods,
    loading: benchmarkRatePeriodsLoading,
    error: benchmarkRatePeriodsError,
  } = useBenchmarkRatePeriods();
  const {
    data: indices,
    loading: indicesLoading,
    error: indicesError,
  } = useIndices();
  const {
    data: baseAssetTypes,
    loading: baseAssetTypesLoading,
    error: baseAssetTypesError,
  } = useBaseAssetTypes();
  const {
    data: creditRiskTypes,
    loading: creditRiskTypesLoading,
    error: creditRiskTypesError,
  } = useCreditRiskTypes();

  const assetTypeOptions = useMemo(
    () =>
      (assetTypes ?? []).map((a) => ({
        id: a.assetTypeId,
        name: a.assetTypeName,
      })),
    [assetTypes],
  );
  const crcyOptions = useMemo(
    () => (crcies ?? []).map((c) => ({ id: c.crcyId, name: c.crcyName })),
    [crcies],
  );
  const benchmarkRateOptions = useMemo(
    () =>
      (benchmarkRates ?? []).map((b) => ({
        id: b.benchmarkRateId,
        name: b.benchmarkRateName,
      })),
    [benchmarkRates],
  );
  const benchmarkRatePeriodOptions = useMemo(
    () =>
      (benchmarkRatePeriods ?? []).map((b) => ({
        id: b.benchmarkRatePeriodId,
        name: b.benchmarkRatePeriodName,
      })),
    [benchmarkRatePeriods],
  );
  const indexOptions = useMemo(
    () => (indices ?? []).map((i) => ({ id: i.idxId, name: i.idxName })),
    [indices],
  );
  const baseAssetTypeOptions = useMemo(
    () =>
      (baseAssetTypes ?? []).map((b) => ({
        id: b.baseAssetTypeId,
        name: b.baseAssetTypeName,
      })),
    [baseAssetTypes],
  );
  const creditRiskTypeOptions = useMemo(
    () =>
      (creditRiskTypes ?? []).map((c) => ({
        id: c.creditRiskTypeId,
        name: c.creditRiskTypeName,
      })),
    [creditRiskTypes],
  );

  const fieldDefs = useMemo<LopFieldDef[]>(() => {
    const SID = t('assets.sections.identity');
    const SCLASS = t('assets.sections.classification');
    const STRADE = t('assets.sections.trading');
    const SBI = t('assets.sections.benchmarkIndustry');
    const defs: LopFieldDef[] = [
      // ── זיהוי הנייר ──
      {
        field: 'assetId',
        label: t('assets.columns.assetId'),
        type: 'number',
        required: true,
        editableOnAddOnly: true,
        section: SID,
      },
      { field: 'assetName', label: t('assets.columns.assetName'), type: 'text', section: SID },
      { field: 'shortName', label: t('assets.columns.shortName'), type: 'text', section: SID },
      { field: 'ticker', label: t('assets.columns.ticker'), type: 'text', section: SID },
      { field: 'symbol', label: t('assets.columns.symbol'), type: 'text', section: SID },
      { field: 'isin', label: t('assets.columns.isin'), type: 'text', section: SID },
      { field: 'isin4Isa', label: t('assets.columns.isin4Isa'), type: 'text', section: SID },

      // ── סיווג ──
      {
        field: 'assetTypeId',
        label: t('assets.columns.assetTypeId'),
        type: 'fk',
        required: true,
        fkOptions: assetTypeOptions,
        section: SCLASS,
      },
      {
        field: 'baseAssetTypeId',
        label: t('assets.columns.baseAssetTypeId'),
        type: 'fk',
        fkAllowNone: true,
        fkOptions: baseAssetTypeOptions,
        section: SCLASS,
      },
      {
        field: 'creditRiskTypeId',
        label: t('assets.columns.creditRiskTypeId'),
        type: 'fk',
        fkAllowNone: true,
        fkOptions: creditRiskTypeOptions,
        section: SCLASS,
      },
      { field: 'isForeign', label: t('assets.columns.isForeign'), type: 'bit', section: SCLASS },
      { field: 'isGovernment', label: t('assets.columns.isGovernment'), type: 'bit', section: SCLASS },

      // ── מסחר ומטבע ──
      {
        field: 'crcyId',
        label: t('assets.columns.crcyId'),
        type: 'fk',
        fkAllowNone: true,
        fkOptions: crcyOptions,
        section: STRADE,
      },
      {
        field: 'linkageCrcyId',
        label: t('assets.columns.linkageCrcyId'),
        type: 'fk',
        fkAllowNone: true,
        fkOptions: crcyOptions,
        section: STRADE,
      },
      {
        field: 'idxId',
        label: t('assets.columns.idxId'),
        type: 'fk',
        fkAllowNone: true,
        fkOptions: indexOptions,
        section: STRADE,
      },
      { field: 'expDate', label: t('assets.columns.expDate'), type: 'date', section: STRADE },
      { field: 'strike', label: t('assets.columns.strike'), type: 'number', section: STRADE },
      { field: 'value1Point', label: t('assets.columns.value1Point'), type: 'number', section: STRADE },
      { field: 'callOrPut', label: t('assets.columns.callOrPut'), type: 'text', section: STRADE },
      { field: 'underlyingAssetId', label: t('assets.columns.underlyingAssetId'), type: 'number', section: STRADE },
      {
        field: 'bloombergTradePlaceId',
        label: t('assets.columns.bloombergTradePlaceId'),
        type: 'text',
        section: STRADE,
      },
      { field: 'countryCode', label: t('assets.columns.countryCode'), type: 'text', section: STRADE },

      // ── ריבית, ענף ומאפיינים ──
      {
        field: 'benchmarkRateId',
        label: t('assets.columns.benchmarkRateId'),
        type: 'fk',
        fkAllowNone: true,
        fkOptions: benchmarkRateOptions,
        section: SBI,
      },
      {
        field: 'benchmarkRatePeriodId',
        label: t('assets.columns.benchmarkRatePeriodId'),
        type: 'fk',
        fkAllowNone: true,
        fkOptions: benchmarkRatePeriodOptions,
        section: SBI,
      },
      {
        field: 'isDynamicInterest',
        label: t('assets.columns.isDynamicInterest'),
        type: 'bit',
        section: SBI,
      },
      {
        field: 'isPreMarketExchange',
        label: t('assets.columns.isPreMarketExchange'),
        type: 'bit',
        section: SBI,
      },
      {
        field: 'industryGroupId',
        label: t('assets.columns.industryGroupId'),
        type: 'number',
        section: SBI,
      },
      {
        field: 'industrySectorId',
        label: t('assets.columns.industrySectorId'),
        type: 'number',
        section: SBI,
      },
    ];
    // Dynamic visibility by asset type — hidden fields fall away per the
    // applicability map (all fields shown for all types today; tighten there).
    return defs.map((def) => ({
      ...def,
      hidden: (rec: Record<string, any>) => !isAssetFieldApplicable(def.field, rec.assetTypeId),
    }));
  }, [
    assetTypeOptions,
    crcyOptions,
    benchmarkRateOptions,
    benchmarkRatePeriodOptions,
    indexOptions,
    baseAssetTypeOptions,
    creditRiskTypeOptions,
  ]);

  const error =
    assetTypesError ??
    crciesError ??
    benchmarkRatesError ??
    benchmarkRatePeriodsError ??
    indicesError ??
    baseAssetTypesError ??
    creditRiskTypesError;
  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('assets.errorPrefix', { message: error })}
        </div>
      </div>
    );
  }

  if (
    assetTypesLoading ||
    crciesLoading ||
    benchmarkRatesLoading ||
    benchmarkRatePeriodsLoading ||
    indicesLoading ||
    baseAssetTypesLoading ||
    creditRiskTypesLoading ||
    !assetTypes ||
    !crcies ||
    !benchmarkRates ||
    !benchmarkRatePeriods ||
    !indices ||
    !baseAssetTypes ||
    !creditRiskTypes
  ) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>
    );
  }

  return (
    <LopRecordForm<Asset>
      title={t('assets.formTitle')}
      fieldDefs={fieldDefs}
      idField="assetId"
      searchTable="Assets_view"
      searchColumns={[
        { field: 'assetId', headerName: t('assets.columns.assetId') },
        { field: 'assetName', headerName: t('assets.columns.assetName') },
        { field: 'symbol', headerName: t('assets.columns.symbol') },
      ]}
      suggestionLabel={(r) => `${r.assetId} - ${r.assetName ?? ''}`}
      loadRecord={getAsset}
      saveRecord={async (rec, isNew) => {
        // assetId is a natural PK supplied by the user, so it's known up-front —
        // find the persisted row by it (falls back to the sent record).
        const list = isNew
          ? await saveAssetsBatch([rec], [], [])
          : await saveAssetsBatch([], [rec], []);
        return list.find((a) => a.assetId === rec.assetId) ?? rec;
      }}
      newRecord={newAsset}
      initialId={initialId}
      searchPlaceholder={t('assets.searchPlaceholder')}
      // Decision #99: tell the user whether the paper is shared or private to this
      // tenant; tenant assets are created by the deal flows, so here they are read-only.
      headerBadge={(rec, mode) =>
        mode === 'edit' ? (
          <span
            data-testid="asset-source-badge"
            style={{
              fontSize: 12,
              padding: '2px 8px',
              borderRadius: 10,
              background: rec.assetSource === 'tenant' ? 'var(--color-warning-bg)' : 'var(--color-info-bg)',
              color: rec.assetSource === 'tenant' ? 'var(--color-warning-text)' : 'var(--color-info-text)',
            }}
          >
            {rec.assetSource === 'tenant'
              ? t('assets.source.tenant')
              : t('assets.source.central')}
          </span>
        ) : null
      }
      headerIdentity={(rec, isNew) => {
        if (isNew) return { name: null, chips: [] };
        const typeName = assetTypeOptions.find((x) => x.id === rec.assetTypeId)?.name;
        const crcyName = rec.crcyId == null ? undefined : crcyOptions.find((x) => x.id === rec.crcyId)?.name;
        return {
          name: rec.assetName || null,
          chips: [
            { key: 'assetId', label: t('assets.chipAssetNo'), value: rec.assetId > 0 ? String(rec.assetId) : '', icon: Hash, inline: true },
            { key: 'assetType', label: t('assets.columns.assetTypeId'), value: typeName ?? '', icon: Layers },
            { key: 'crcy', label: t('assets.columns.crcyId'), value: crcyName ?? '', icon: Coins },
          ],
        };
      }}
      isRecordReadOnly={(rec, mode) => mode === 'edit' && rec.assetSource === 'tenant'}
      readOnlyNote={t('assets.source.tenantReadOnlyNote')}
      onRecordChange={(record, mode) => {
        setTabTitle(
          mode === 'edit' && record.assetId > 0
            ? `${t('assets.formTitle')} · ${record.assetName || record.assetId}`
            : t('assets.formTitle'),
        );
      }}
    />
  );
}
